import React from 'react'
import { EventManagerConfiguration } from './__internals__/EventManager'
import AnalyticsContext from './AnalyticsContext'
import LocationTracker from './LocationTracker'
import useAnalytics from './useAnalytics'

type AnalyticsProviderComponent = (props: {
  children: React.ReactNode
  trackLocations?: boolean
  eventManagerConfiguration: EventManagerConfiguration
}) => JSX.Element

/** Provides Firefly Internal Insights configuration to child `useAnalytics`/`withAnalytics` calls.
 * Provides automated route tracking when enabled with `trackLocations`.
 *
 * @param trackLocations Automatically send page load / route change events to Firefly Internal Insights.
 * @param eventManagerConfiguration Configuration values for creating a Firefly Event Manager.
 * @example
 * const App = () => {
 *   return (
 *     <Router>
 *       <AnalyticsProvider
 *         eventManagerConfiguration={apiConfig.analytics}
 *         trackLocations
 *       >
 *         <Home />
 *       </AnalyticsProvider>
 *     </Router>
 *   )
 * }
 */
const AnalyticsProvider: AnalyticsProviderComponent = ({
  children,
  trackLocations,
  eventManagerConfiguration,
}) => {
  if (!eventManagerConfiguration) {
    throw new Error(
      `'<AnalyticsProvider>' Event manager configuration was not defined. Properties 'apiKey' and 'appName' must contain a value`,
    )
  }
  if (!eventManagerConfiguration.apiKey || !eventManagerConfiguration.appName) {
    throw new Error(
      `'<AnalyticsProvider>' Event manager configuration was formatted incorrectly. Properties 'apiKey' and 'appName' must contain a value`,
    )
  }
  const analytics = useAnalytics(eventManagerConfiguration)

  return (
    <AnalyticsContext.Provider value={analytics}>
      {trackLocations && <LocationTracker />}
      {children}
    </AnalyticsContext.Provider>
  )
}

export default AnalyticsProvider
